import React, { useState, useEffect, useMemo } from "react";
import {
  Calendar,
  MapPin,
  Search,
  RefreshCw,
  LogOut,
  Clock,
  ArrowLeft,
  Sparkles,
} from "lucide-react";

// Generate realistic 14-day rolling dummy schedule data
const STORES = [
  "Downtown Sovetov",
  "Bon Passage Mall",
  "Novorossiysk Centre",
  "Green Park Branch",
  "Galleria Mall",
  "OZ Mall Waterfront",
  "Black Sea Coast",
  "Central Kiosk",
];

const EMPLOYEES = [
  "Alex Morgan",
  "David Gao",
  "Sarah Jenkins",
  "Elena Rostova",
  "Marcus Vance",
  "Chloe Bennett",
  "Lucas Wright",
  "Ryan Chen",
  "Emma Watson",
  "Liam Taylor",
  "Olivia Martinez",
  "Ethan Davis",
];

const SHIFT_TYPES = [
  { name: "Morning (08:00 - 16:00)", isDay: true },
  { name: "Mid-Day (11:00 - 19:00)", isDay: true },
  { name: "Evening (14:00 - 22:00)", isDay: false },
];

function generateMockShifts() {
  const shifts = [];
  const today = new Date();
  
  for (let d = 0; d < 14; d++) {
    const shiftDate = new Date(today);
    shiftDate.setDate(today.getDate() + d);
    const dateStr = shiftDate.toISOString().split("T")[0];

    // Assign ~4-5 staff per day across stores
    EMPLOYEES.forEach((emp, empIdx) => {
      // Deterministic shift schedule based on day and index
      if ((d + empIdx) % 7 !== 0 && (d + empIdx) % 7 !== 6) {
        const store = STORES[(empIdx + d) % STORES.length];
        const shiftType = SHIFT_TYPES[(empIdx + d * 2) % SHIFT_TYPES.length];
        shifts.push({
          name: emp,
          date: dateStr,
          store,
          time: shiftType.name,
          isDay: shiftType.isDay,
        });
      }
    });
  }
  return shifts;
}

const MOCK_SHIFTS = generateMockShifts();

const App = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shifts, setShifts] = useState(MOCK_SHIFTS);

  useEffect(() => {
    if (!document.getElementById("tailwind-cdn")) {
      const script = document.createElement("script");
      script.id = "tailwind-cdn";
      script.src = "https://cdn.tailwindcss.com";
      document.head.appendChild(script);
    }

    const style = document.createElement("style");
    style.innerHTML = `
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
      body { font-family: 'Inter', sans-serif !important; background-color: #f8fafc; }
    `;
    document.head.appendChild(style);
  }, []);

  const employeeNames = useMemo(() => {
    const names = new Set(shifts.map((d) => d.name));
    return Array.from(names).sort();
  }, [shifts]);

  const userShifts = useMemo(() => {
    if (!selectedUser) return [];
    return shifts
      .filter((d) => d.name === selectedUser)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [shifts, selectedUser]);

  const handleRefresh = () => {
    setIsLoading(true);
    setTimeout(() => {
      setShifts(generateMockShifts());
      setIsLoading(false);
    }, 400);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-16">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 px-4 py-3 flex items-center justify-between shadow-xs">
        <div className="flex items-center gap-3">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold transition-all"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Portfolio
          </a>
          <div className="h-5 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 p-2 rounded-lg text-white shadow-xs">
              <Calendar className="w-4 h-4" />
            </div>
            <div>
              <h1 className="text-sm font-bold tracking-tight text-slate-900 leading-tight">
                Boba Cafe &middot; Staff Schedule
              </h1>
              <p className="text-[11px] text-slate-400 font-medium">
                Multi-Store Shift Management &middot; Interactive Demo
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all active:scale-95 disabled:opacity-40"
          title="Refresh Schedule Data"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
        </button>
      </header>

      <main className="max-w-xl mx-auto p-4 pt-6">
        {/* Banner badge */}
        <div className="mb-6 p-3 bg-blue-50/70 border border-blue-100 rounded-xl flex items-center gap-2.5 text-xs text-blue-800">
          <Sparkles className="w-4 h-4 text-blue-600 shrink-0" />
          <span>
            <strong>Production Internal Tool Demo:</strong> Shift assignments generated via OR-Tools constraint solver balancing availability & rest intervals.
          </span>
        </div>

        {!selectedUser ? (
          <div className="space-y-6 mt-4">
            <div className="text-center space-y-1.5">
              <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
                Who's Working?
              </h2>
              <p className="text-slate-500 font-normal text-sm">
                Select a team member to view their 14-day assigned store shifts.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="relative">
                <select
                  className="w-full p-4 pl-12 bg-transparent appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-semibold text-slate-800 cursor-pointer"
                  onChange={(e) => setSelectedUser(e.target.value)}
                  value=""
                >
                  <option value="" disabled>
                    Select Team Member...
                  </option>
                  {employeeNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            {/* Quick-select chips */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Quick Select:
              </p>
              <div className="flex flex-wrap gap-2">
                {employeeNames.slice(0, 6).map((name) => (
                  <button
                    key={name}
                    onClick={() => setSelectedUser(name)}
                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs font-medium text-slate-700 hover:border-blue-400 hover:text-blue-600 transition-all shadow-2xs"
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-center gap-4 text-xs text-slate-400 font-medium pt-4">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Live Demo Data
              </span>
              <span>&bull;</span>
              <span>{shifts.length} Total Shift Slots</span>
              <span>&bull;</span>
              <span>{STORES.length} Store Locations</span>
            </div>
          </div>
        ) : (
          <div className="space-y-6 mt-2">
            <div className="flex justify-between items-start">
              <div>
                <span className="text-blue-600 font-bold text-[11px] uppercase tracking-wider mb-1 block bg-blue-50 px-2.5 py-0.5 rounded-full w-fit">
                  Team Member Schedule
                </span>
                <h2 className="text-2xl font-bold text-slate-900 mt-1.5">
                  {selectedUser}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {userShifts.length} upcoming shifts scheduled across branches
                </p>
              </div>
              <button
                onClick={() => setSelectedUser(null)}
                className="inline-flex items-center gap-1 px-3 py-2 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-lg text-xs font-semibold transition-all active:scale-95 shadow-2xs"
                title="Switch Employee"
              >
                <LogOut className="w-3.5 h-3.5" />
                Change
              </button>
            </div>

            <div className="space-y-3">
              {userShifts.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
                  <p className="text-slate-400 font-medium text-sm">
                    No shifts assigned in the next 14 days.
                  </p>
                </div>
              ) : (
                userShifts.map((shift, idx) => {
                  const dObj = new Date(shift.date + "T12:00:00");
                  const monthStr = dObj.toLocaleDateString("en-US", { month: "short" });
                  const dayNum = dObj.getDate();
                  const weekdayStr = dObj.toLocaleDateString("en-US", { weekday: "long" });

                  return (
                    <div
                      key={idx}
                      className="bg-white p-4 rounded-2xl shadow-xs border border-slate-200 flex flex-col gap-3 hover:shadow-md hover:border-blue-200 transition-all"
                    >
                      <div className="flex items-center gap-3.5">
                        <div className="w-12 h-12 bg-blue-600 text-white rounded-xl flex flex-col items-center justify-center shadow-xs shrink-0">
                          <span className="text-[9px] font-bold uppercase opacity-80 leading-none mb-0.5">
                            {monthStr}
                          </span>
                          <span className="text-base font-extrabold leading-none">
                            {dayNum}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-0.5">
                            {weekdayStr}
                          </p>
                          <p className="text-sm font-bold text-slate-900 truncate">
                            {shift.date}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2.5 pt-1 border-t border-slate-100">
                        <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                          <div className="flex items-center gap-1 text-slate-400 mb-0.5">
                            <MapPin className="w-3 h-3 text-blue-600" />
                            <span className="text-[10px] font-bold uppercase tracking-wider">
                              Branch
                            </span>
                          </div>
                          <p className="text-xs font-bold text-slate-800 truncate">
                            {shift.store}
                          </p>
                        </div>
                        <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                          <div className="flex items-center gap-1 text-slate-400 mb-0.5">
                            <Clock className="w-3 h-3 text-blue-600" />
                            <span className="text-[10px] font-bold uppercase tracking-wider">
                              Hours
                            </span>
                          </div>
                          <p
                            className={`text-xs font-bold truncate ${
                              shift.isDay ? "text-blue-700" : "text-amber-700"
                            }`}
                          >
                            {shift.time}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <button
              onClick={() => {
                setSelectedUser(null);
                window.scrollTo(0, 0);
              }}
              className="w-full py-3 bg-white border border-slate-200 text-slate-600 font-semibold text-xs uppercase tracking-wider hover:bg-slate-50 rounded-xl transition-colors shadow-2xs"
            >
              &larr; Select Another Team Member
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default App;
